// Small formatting helpers shared by the Dealer Stock management screens.

export const formatDate = (value) => {
  if (!value) return "";
  // Date-only strings are built as local dates so they do not print a day early.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

export const todayYmd = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const vehicleLabel = (record) => {
  const v = record && record.vehicle;
  if (v && v.title) return v.title;
  if (v && (v.year || v.make || v.model)) return [v.year, v.make, v.model].filter(Boolean).join(" ");
  return record && record.vehicleId ? `Vehicle ${record.vehicleId}` : "General";
};

export const customerLabel = (record) => {
  const u = record && record.userId;
  if (u && typeof u === "object") {
    const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
    return name ? `${name} (${u.email})` : u.email;
  }
  const name = [record.firstName, record.lastName].filter(Boolean).join(" ");
  return name ? `${name} (${record.email}) - guest` : `${record.email || "unknown"} - guest`;
};

// Short name for a board card: "Cam Customer", or the guest's email.
export const customerName = (record) => {
  const u = record && record.userId;
  if (u && typeof u === "object") return [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
  return [record.firstName, record.lastName].filter(Boolean).join(" ") || record.email || "Guest";
};

export const customerIdOf = (record) => {
  const u = record && record.userId;
  if (!u) return "";
  return typeof u === "object" ? u._id : u;
};

// How to ask the API for the customer behind a record: their account when the
// record knows it, else the lead or inspection itself (`kind`: "inquiry" or
// "inspection"; the server finds an account under its email, if any). Records
// that always have an account (purchases, invoices, favourites) need no kind.
export const customerRefOf = (record, kind) => {
  const userId = customerIdOf(record) || (record && record.accountUserId);
  if (userId) return { userId: String(userId) };
  if (kind === "inquiry" && record && record._id) return { inquiryId: String(record._id) };
  if (kind === "inspection" && record && record._id) return { inspectionId: String(record._id) };
  return null;
};

// US numbers as (703) 249-9922, as the emails and invoice PDF print them
// (Backend/app/utils/phone.js); anything else exactly as typed.
export const formatPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  const us = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
  if (us.length === 10) return `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}`;
  return String(raw || "").trim();
};

// Whole days since the record's last step (or since it was created).
export const daysInStep = (record) => {
  const history = (record && record.statusHistory) || [];
  const last = history.length ? history[history.length - 1].at : (record.updatedAt || record.createdAt);
  const t = last ? new Date(last).getTime() : NaN;
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

// "24,500" / "$24,500.00" -> 24500; "" -> null; anything else -> NaN.
export const parseMoney = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

export const invoiceRef = (invoice) => {
  if (!invoice) return "";
  if (invoice.source === "quickbooks") return `QuickBooks #${invoice.externalRef}`;
  return invoice.number || "Draft";
};

export const isOverdue = (invoice) =>
  invoice && invoice.status === "sent" && invoice.dueDate && invoice.dueDate < todayYmd();

export const errorMessage = (res, fallback) => (res && res.message ? res.message : fallback);

// Said when a payment clears a car invoice and the sale leaves Awaiting Payment.
export const ORDER_CONFIRMED_NOTE = "Paid in full: the sale moved to Order Confirmed and the customer was sent their order confirmation.";

// Whole days since a date: "today", "1d", "12d"; "" when there is no date.
export const ageLabel = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  if (Number.isNaN(t)) return "";
  const days = Math.floor((Date.now() - t) / 86400000);
  return days <= 0 ? "today" : `${days}d`;
};

// How long ago, for a report time: "just now", "25 min ago", "5 h ago", "3 days ago".
export const sinceLabel = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  if (Number.isNaN(t)) return "";
  const minutes = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

// Hours since a date (Infinity when there is none).
export const hoursSince = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(t) ? Infinity : (Date.now() - t) / 3600000;
};

// A lead goes on the board's call list once our reply has gone this long
// without an answer.
export const CALL_AFTER_HOURS = 24;

// Our reply, when it is still the last message in the thread: the customer
// hasn't answered it. Every staff reply is emailed to the customer.
export const unansweredReply = (inquiry) => {
  const messages = (inquiry && inquiry.messages) || [];
  const last = messages[messages.length - 1];
  return last && last.author === "staff" ? last : null;
};

// Someone phoned the customer after our last unanswered reply.
export const calledSinceReply = (inquiry) => {
  const reply = unansweredReply(inquiry);
  return !!(reply && inquiry.calledAt && new Date(inquiry.calledAt) >= new Date(reply.createdAt));
};

// On the call list: a live conversation whose last word is our reply,
// unanswered for CALL_AFTER_HOURS, and nobody has called since. Not while we
// wait on the dealer in Japan, have an inspection open for the customer, or
// hold their deposit - then the next move is ours, not theirs. (The API adds
// hasOpenInspection and depositBalance to each lead.)
export const needsCall = (inquiry) => {
  if (!inquiry || inquiry.status !== "contacted" || inquiry.archivedAt) return false;
  if (inquiry.dealerAsk && inquiry.dealerAsk.askedAt) return false;
  if (inquiry.hasOpenInspection || inquiry.depositBalance > 0) return false;
  const reply = unansweredReply(inquiry);
  return !!reply && hoursSince(reply.createdAt) >= CALL_AFTER_HOURS && !calledSinceReply(inquiry);
};

// A customer reply unanswered this long is overdue (the board's cards turn red too).
export const REPLY_ALERT_HOURS = 48;

// The one thing to tell staff about a lead, most pressing first, for the line
// under the lead panel's header; null when there's nothing. `since` is when it
// started. Kinds:
//   archived - hidden from the board until a sign of life
//   call     - on the call list: phone them
//   yourTurn - the customer wrote last
//   dealer   - waiting on the dealer in Japan
//   called   - phoned since our last reply (with Undo for a mis-click)
export const leadAttention = (inquiry) => {
  if (!inquiry) return null;
  if (inquiry.archivedAt) return { kind: "archived", since: inquiry.archivedAt };
  if (needsCall(inquiry)) return { kind: "call", since: unansweredReply(inquiry).createdAt };
  const messages = inquiry.messages || [];
  const last = messages[messages.length - 1];
  if (["contacted", "searching"].includes(inquiry.status) && last && last.author === "customer") {
    return { kind: "yourTurn", since: last.createdAt };
  }
  if (inquiry.status !== "closed" && inquiry.dealerAsk && inquiry.dealerAsk.askedAt) {
    return { kind: "dealer", since: inquiry.dealerAsk.askedAt };
  }
  if (inquiry.status === "contacted" && calledSinceReply(inquiry)) return { kind: "called", since: inquiry.calledAt };
  return null;
};

// The same line for an inspection, most pressing first; null when there's
// nothing. `thread` is the request's lead (its conversation), when it has one.
// Kinds:
//   yourTurn - the customer wrote last in the conversation
//   deposit  - we asked for the deposit and it hasn't arrived
//   dealer   - waiting on the dealer in Japan
//   booked   - the booked date (`since` is that YYYY-MM-DD): coming up,
//              today, or gone by with the report still to write
//   deciding - the report is done and they haven't answered
export const inspectionAttention = (inspection, thread) => {
  if (!inspection || inspection.status === "Cancelled") return null;
  const messages = (thread && thread.messages) || [];
  const last = messages[messages.length - 1];
  if (last && last.author === "customer") return { kind: "yourTurn", since: last.createdAt };
  const open = ["Requested", "Accepted", "Scheduled"].includes(inspection.status);
  const ask = inspection.depositRequest || {};
  if (open && ask.requestedAt && !ask.paidAt) return { kind: "deposit", since: ask.requestedAt };
  if (open && inspection.dealerAsk && inspection.dealerAsk.askedAt) return { kind: "dealer", since: inspection.dealerAsk.askedAt };
  if (inspection.status === "Scheduled" && inspection.scheduledFor) return { kind: "booked", since: inspection.scheduledFor };
  if (inspection.status === "Completed" && !(inspection.decision && inspection.decision.value)) {
    return { kind: "deciding", since: inspection.completedAt };
  }
  return null;
};

// Whole days from today to a YYYY-MM-DD date: 0 today, 2 in two days, -3
// three days ago; null when it isn't a date.
export const daysUntil = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((new Date(+m[1], +m[2] - 1, +m[3]) - start) / 86400000);
};
